import { Experiment, ExperimentClient, ExperimentConfig } from '@amplitude/experiment-js-client'
import { EXPERIMENT_LOCAL_OVERRIDES, EXPERIMENT_VARIANT_TIMEOUT_MS, ExperimentDefinition } from '@/config/experiments.config'
import { segment } from './tracking.utils'
import { getAnonymousSegmentId } from './ids.utils'

/**
 * `Experiment.initialize` keeps one client per deployment key, so whichever call comes
 * first fixes the config for every experiment — keep it in one place.
 *
 * Exposures are reported explicitly with `trackExperimentExposure`, never on `variant()`:
 * resolving a variant happens long before the user can see the arm they were put in.
 */
const EXPERIMENT_CLIENT_CONFIG: ExperimentConfig = {
  automaticExposureTracking: false,
  exposureTrackingProvider: { track: (exposure) => segment.exposure(exposure) },
}

const getExperimentClient = (): ExperimentClient | undefined => {
  const amplitudeDeploymentKey = process.env.NEXT_PUBLIC_AMPLITUDE_DEPLOYMENT_KEY
  return amplitudeDeploymentKey ? Experiment.initialize(amplitudeDeploymentKey, EXPERIMENT_CLIENT_CONFIG) : undefined
}

const getLocalOverride = (experiment: ExperimentDefinition): string | undefined => {
  const override = EXPERIMENT_LOCAL_OVERRIDES[experiment.key]
  return override && experiment.variants.includes(override) ? override : undefined
}

/**
 * Resolves the active variant for an experiment.
 *
 * Local overrides win; otherwise the Amplitude Experiment client decides, falling back to
 * `experiment.defaultVariant` so an unreachable/undecided flag always renders the current
 * behavior. Resolving reports no exposure — see `trackExperimentExposure`.
 *
 * Amplitude buckets on Segment's anonymous id, the id our exposures reach Amplitude under.
 * Without it the user isn't bucketed at all: this rejects, and callers fall back to the
 * default variant without reporting an exposure.
 *
 * We track through Segment, not the Amplitude Analytics SDK, so use plain
 * `Experiment.initialize()` with an `exposureTrackingProvider` that forwards
 * exposures to Segment — NOT `initializeWithAmplitudeAnalytics()`, which requires
 * `@amplitude/analytics-browser` to be installed and initialized.
 *
 * @param {ExperimentDefinition} experiment - The experiment to resolve.
 * @param userId
 * @returns {Promise<string>} The active variant, or the experiment's default variant.
 */
export const getExperimentVariant = async ({
  experiment,
  userId,
}: {
  experiment: ExperimentDefinition
  userId?: string
}): Promise<string> => {
  // Local overrides take precedence over Amplitude, so check for them first. This may be useful for testing and debugging experiments without having to go through Amplitude.
  const override = getLocalOverride(experiment)
  if (override) {
    return Promise.resolve(override)
  }

  // If no local override is found, fetch the variant from Amplitude.
  const experimentClient = getExperimentClient()
  if (!experimentClient) {
    throw new Error('Amplitude deployment key is not defined.')
  }
  // Bucket on Segment's anonymous id: exposures reach Amplitude through Segment under that id, so
  // any other device id files the assignment and the exposure on separate Amplitude profiles.
  // Segment often needs longer than getAnonymousSegmentId's 500ms default to load. The page shows
  // the default variant while we wait, so we can afford the full EXPERIMENT_VARIANT_TIMEOUT_MS.
  const deviceId = await getAnonymousSegmentId(EXPERIMENT_VARIANT_TIMEOUT_MS)
  if (!deviceId) {
    throw new Error('Segment anonymous id unavailable.')
  }
  // eslint-disable-next-line camelcase
  await experimentClient.fetch({ user_id: userId, device_id: deviceId }, { flagKeys: [experiment.key] })

  const variant = experimentClient.variant(experiment.key)
  return variant?.value && experiment.variants.includes(variant.value) ? variant.value : experiment.defaultVariant
}

/**
 * Reports that the user has reached the point where their arm of the experiment is live.
 *
 * Call it only once `getExperimentVariant` has resolved for the current identity. The
 * SDK reports the variant it fetched, with Amplitude's own experiment key and metadata,
 * and drops repeats for the same flag, variant and identity — so re-renders and several
 * callers never double-count a user. Locally overridden experiments never reached
 * Amplitude, so there is nothing to report for them.
 *
 * @param {ExperimentDefinition} experiment - The experiment the user was exposed to.
 */
export const trackExperimentExposure = (experiment: ExperimentDefinition): void => {
  if (getLocalOverride(experiment)) return
  getExperimentClient()?.exposure(experiment.key)
}

export default getExperimentVariant
